import React, { useState } from 'react';
import { Alert } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import { useReportContext } from '../../../_context/ReportContext';
import { ClientType } from '../../../_models/Client.model';

interface ReportNavigationProps {
  onNext?: () => void;
  onPrevious?: () => void;
  canGoNext?: boolean;
  canGoPrevious?: boolean;
  nextLabel?: string;
  previousLabel?: string;
}

export const ReportNavigation: React.FC<ReportNavigationProps> = ({
  onNext,
  onPrevious,
  canGoNext = true,
  canGoPrevious = true,
  nextLabel = "Next",
  previousLabel = "Previous"
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { newReport, viewedAreas } = useReportContext();
  const [errorMessage, setErrorMessage] = useState("");

  // Define the navigation flow based on client type
  const getNavigationFlow = () => {
    const isNDIS = newReport.Client.Type === ClientType.NDIS;
    const isDomestic = newReport.Client.Type === ClientType.Domestic;

    if (isNDIS) {
      // NDIS includes NDIS questions
      return [
        '/reports/create_new_report',
        '/reports/create_new_report/scoring',
        '/reports/create_new_report/ndis-questions',
        '/reports/create_new_report/comments',
        '/reports/create_new_report/signatures'
      ];
    } else if (isDomestic) {
      // Domestic skips NDIS questions
      return [
        '/reports/create_new_report',
        '/reports/create_new_report/scoring',
        '/reports/create_new_report/comments',
        '/reports/create_new_report/signatures'
      ];
    } else {
      // Other client types include consent but not NDIS questions
      return [
        '/reports/create_new_report',
        '/reports/create_new_report/client-consent',
        '/reports/create_new_report/scoring',
        '/reports/create_new_report/comments',
        '/reports/create_new_report/signatures'
      ];
    }
  };

  const navigationFlow = getNavigationFlow();
  const currentIndex = navigationFlow.findIndex(path => location.pathname === path);

  // Validation logic for each step
  const validateCurrentStep = () => {
    setErrorMessage("");

    if (location.pathname === '/reports/create_new_report/scoring') {
      // 1. Check if all areas are viewed
      if (viewedAreas.size < newReport.Areas.length) {
        setErrorMessage("Please view all Inspection Areas before proceeding.");
        return false;
      }

      // 2. Check if all areas are scored
      const areaBasedScoringTypes = process.env.REACT_APP_AREA_BASED_SCORING_TYPES?.split(",");
      const shouldUseAreaBasedScoring =
        areaBasedScoringTypes?.includes(newReport.Client.Type.toString()) ||
        newReport.Client.Type === ClientType.NDIS ||
        newReport.Client.Type === ClientType.Domestic;

      if (shouldUseAreaBasedScoring) {
        // For area-based scoring, check if all areas have scores
        // We ensure we don't accidentally fail if an Area has 0 SubAreas, but if it has items, they must be set (we assume un-scored is represented as a specific negative value or undefined, but looking at ClientItemScoring, N/A sets it to 0. We'll allow item.Mark >= 0 as long as it's not null/undefined)
        const allAreasScored = newReport.Areas.every(area => {
          if (!area.SubAreas || area.SubAreas.length === 0) return true;
          return area.SubAreas.every(subArea => {
            if (!subArea.Items || subArea.Items.length === 0) return true;
            return subArea.Items.every(item => item.Mark !== undefined && item.Mark !== null && item.Mark >= 0);
          });
        });

        if (!allAreasScored) {
          setErrorMessage("All areas need to be scored before proceeding!");
          return false;
        }
      } else {
        // For detailed scoring, check if all items have scores (including N/A which is 0)
        const allItemsScored = newReport.Areas.every(area => {
          if (!area.SubAreas || area.SubAreas.length === 0) return true;
          return area.SubAreas.every(subArea => {
            if (!subArea.Items || subArea.Items.length === 0) return true;
            return subArea.Items.every(item => item.Mark !== undefined && item.Mark !== null && item.Mark >= 0);
          });
        });

        if (!allItemsScored) {
          setErrorMessage("All items need to be scored before proceeding!");
          return false;
        }
      }
    }

    if (location.pathname === '/reports/create_new_report/ndis-questions') {
      // Check if NDIS questions are answered
      if (newReport.NDISIncidentOccurred === undefined || newReport.NDISPPEUsed === undefined) {
        setErrorMessage("Please answer both NDIS safety questions before proceeding!");
        return false;
      }
    }

    return true;
  };

  const handlePrevious = () => {
    if (onPrevious) {
      onPrevious();
    } else if (currentIndex > 0) {
      navigate(navigationFlow[currentIndex - 1]);
    }
  };

  const handleNext = () => {
    if (validateCurrentStep()) {
      if (onNext) {
        onNext();
      } else if (currentIndex < navigationFlow.length - 1) {
        navigate(navigationFlow[currentIndex + 1]);
      }
      // For the last step (signatures), we don't navigate - the Review Report button handles that
    } else {
      window.scrollTo(0, 0);
    }
  };

  const isFirstStep = currentIndex === 0;
  const isLastStep = currentIndex === navigationFlow.length - 1;

  // Custom labels based on current step
  const getNextLabel = () => {
    if (isLastStep) return "Next"; // Don't show "Review Report" here since we have a separate button

    switch (location.pathname) {
      case '/reports/create_new_report':
        return "Start Report";
      case '/reports/create_new_report/scoring':
        return newReport.Client.Type === ClientType.NDIS ? "NDIS Questions" : "Comments";
      case '/reports/create_new_report/ndis-questions':
        return "Comments";
      case '/reports/create_new_report/comments':
        return "Signatures";
      default:
        return nextLabel;
    }
  };

  const getPreviousLabel = () => {
    switch (location.pathname) {
      case '/reports/create_new_report/scoring':
        return "Back to Client";
      case '/reports/create_new_report/ndis-questions':
        return "Back to Scoring";
      case '/reports/create_new_report/comments':
        return newReport.Client.Type === ClientType.NDIS ? "Back to NDIS Questions" : "Back to Scoring";
      case '/reports/create_new_report/signatures':
        return "Back to Comments";
      default:
        return previousLabel;
    }
  };

  return (
    <div className="reports-page-wrapper" style={{ maxWidth: 900, marginTop: "32px", marginBottom: "64px", background: "transparent", padding: 0, boxShadow: "none" }}>
      {errorMessage && (
        <div style={{ animation: "auFadeIn 0.3s ease", marginBottom: "24px" }}>
          <Alert variant="danger" onClose={() => setErrorMessage("")} dismissible style={{ borderRadius: "8px", border: "1px solid #fecaca", fontWeight: 500, margin: 0 }}>
            {errorMessage}
          </Alert>
        </div>
      )}

      {!isFirstStep && (
        <div className="rp-bottom-nav">

          <button
            onClick={handlePrevious}
            disabled={!canGoPrevious}
            className="rp-bottom-nav-btn"
            style={{
              background: "#fff",
              color: "#4B5563",
              border: "1px solid #D1D5DB",
              cursor: canGoPrevious ? "pointer" : "not-allowed",
              opacity: canGoPrevious ? 1 : 0.6,
            }}
            onMouseEnter={(e) => { if (canGoPrevious) { e.currentTarget.style.background = "#F9FAFB"; e.currentTarget.style.color = "#111827"; } }}
            onMouseLeave={(e) => { if (canGoPrevious) { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#4B5563"; } }}
          >
            <FaArrowLeft />
            {getPreviousLabel()}
          </button>

          {!isLastStep && (() => {
            const isScoringPage = location.pathname === '/reports/create_new_report/scoring';
            const hasUnviewedAreas = isScoringPage && viewedAreas.size < newReport.Areas.length;
            const visuallyDisabled = !canGoNext || hasUnviewedAreas;

            return (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                {hasUnviewedAreas && (
                  <span style={{ fontSize: "0.75rem", color: "#EF4444", fontWeight: 600, marginBottom: "4px", textAlign: "center", lineHeight: "1.2" }}>
                    View Inspection Areas<br />to proceed
                  </span>
                )}
                <button
                  className="au-submit-btn hover-trigger rp-bottom-nav-btn"
                  onClick={handleNext}
                  disabled={visuallyDisabled}
                  style={{
                    background: visuallyDisabled ? "#E5E7EB" : "linear-gradient(135deg, #2563C0 0%, #1d4ed8 100%)",
                    color: visuallyDisabled ? "#9CA3AF" : "#fff",
                    border: "none",
                    cursor: visuallyDisabled ? "not-allowed" : "pointer",
                    opacity: visuallyDisabled ? 1 : (canGoNext ? 1 : 0.6),
                    boxShadow: visuallyDisabled ? "none" : "0 4px 12px rgba(37,99,192,0.3)",
                    marginTop: 0,
                  }}
                >
                  {getNextLabel()}
                  <FaArrowRight />
                </button>
              </div>
            );
          })()}

        </div>
      )}
    </div>
  );
}; 